import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { store, useDB } from '../data/store';
import { allTags, isFollowing, memberRole, userById, visiblePosts } from '../data/queries';
import { Composer } from '../components/Post';
import { ExampleLabel, Rainbow, RichText, TagLink, YearLink, timeAgo } from '../components/Bits';
import { useUI } from '../components/ui';
import { FeedView } from './Feeds';
import { NotFound } from './Static';
import { colorFor } from '../lib/text';
import { YEARS } from '../lib/years';

/* ---------------- Ideas: questions anyone can start, answered with memories ---------------- */
export function Ideas() {
  const db = useDB(), ui = useUI(), nav = useNavigate();
  const [params] = useSearchParams();
  const [text, setText] = useState(''), [year, setYear] = useState(params.get('year') ?? '');
  const [err, setErr] = useState('');
  const answers = (id: string) => visiblePosts(db).filter(p => p.ideaId === id).length;
  const list = db.ideas.filter(i => !(db.settings.hideExamples && i.example));
  const submit = (e: FormEvent) => {
    e.preventDefault(); setErr('');
    const input = { text, year: year ? Number(year) : undefined };
    const r = store.createIdea(input);
    if (r.ok) { setText(''); nav(`/ideas/${r.value.id}`); }
    else if (r.error === 'NEED_PROFILE') ui.act(() => store.createIdea(input), i => nav(`/ideas/${i.id}`));
    else setErr(r.fields?.text ?? r.error);
  };
  return (
    <div className="narrow col">
      <div className="hero"><span className="line">Ideas</span><h1>Start a <Rainbow text="question" />. Everyone answers with a memory.</h1>
        <p>"What was your first ringtone?" "What's the most 1999 thing?" Ask anything about the past. Add #hashtags so people find it.</p></div>
      <form className="card form" onSubmit={submit}>
        <label className="fld"><span>Your idea</span><input value={text} onChange={e => setText(e.target.value)} maxLength={140} placeholder="What was in your pencil case in 1999? #school" aria-invalid={!!err} /></label>
        <div className="crow">
          <select value={year} onChange={e => setYear(e.target.value)} aria-label="About a year (optional)"><option value="">Any year</option>{YEARS.slice().reverse().map(y => <option key={y}>{y}</option>)}</select>
          <span className="grow" />{err && <em className="error">{err}</em>}<button className="btn hot">Start the idea</button>
        </div>
      </form>
      <ul className="list">{list.map(i => {
        const a = userById(db, i.authorId);
        return (<li key={i.id} className="card idea-row">
          <Link to={`/ideas/${i.id}`} className="idea-text">{i.text}</Link>
          <div className="meta">@{a?.handle} · {timeAgo(i.createdAt)} {i.year && <>· <YearLink year={i.year} /></>} · {answers(i.id)} answers {i.example && <ExampleLabel />}</div>
          {i.tags.length > 0 && <div className="tags">{i.tags.map(t => <TagLink key={t} tag={t} />)}</div>}
        </li>);
      })}</ul>
    </div>
  );
}

export function IdeaPage() {
  const { id } = useParams();
  const db = useDB();
  const idea = db.ideas.find(i => i.id === id);
  const filter = useMemo(() => ({ ideaId: id }), [id]);
  if (!idea) return <NotFound />;
  const a = userById(db, idea.authorId);
  return (
    <div className="narrow col">
      <div className="hero"><span className="line">Idea by @{a?.handle} {idea.example && <ExampleLabel />}</span><h1 className="ytitle"><RichText text={idea.text} /></h1>
        {idea.year && <p>About <YearLink year={idea.year} /></p>}</div>
      <Composer compact defaults={{ ideaId: idea.id, year: idea.year }} key={idea.id} />
      <FeedView filter={filter} empty="No answers yet. Be the first." endText="That's every answer so far." showBranches={false} />
    </div>
  );
}

/* ---------------- Hashtags ---------------- */
export function Tags() {
  const db = useDB();
  const [q, setQ] = useState('');
  const tags = useMemo(() => allTags(db), [db]);
  const shown = tags.filter(t => t.tag.includes(q.trim().replace(/^#/, '').toLowerCase()));
  return (
    <div className="narrow col">
      <div className="hero"><span className="line">Hashtags</span><h1>Every <Rainbow text="#hashtag" /> people started.</h1>
        <p>To start a new one, write it in a memory, a comment or an idea: <b>#mytag</b>. It appears here and gets its own page.</p></div>
      <label className="fld"><span>Find a hashtag</span><input value={q} onChange={e => setQ(e.target.value)} placeholder="#snowday" /></label>
      <div className="tagcloud">{shown.map(t => (
        <Link key={t.tag} to={`/t/${encodeURIComponent(t.tag)}`} className="tagchip" style={{ color: colorFor(t.tag), borderColor: colorFor(t.tag) }}>
          #{t.tag} <span className="n">{t.uses}</span>{isFollowing(db, 'tag', t.tag) && <span className="small"> · following</span>}
        </Link>))}
        {shown.length === 0 && <p className="muted">No hashtag like that yet. Write it in a memory and it exists.</p>}
      </div>
    </div>
  );
}

/* ---------------- Communities ---------------- */
export function Communities() {
  const db = useDB(), ui = useUI(), nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: '', description: '', rules: '' });
  const [err, setErr] = useState<Record<string, string>>({});
  const list = db.communities.filter(c => !(db.settings.hideExamples && c.example));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const input = { name: f.name, description: f.description, rules: f.rules.split('\n').map(r => r.trim()).filter(Boolean) };
    const r = store.createCommunity(input);
    if (r.ok) nav(`/c/${r.value.slug}`);
    else if (r.error === 'NEED_PROFILE') ui.act(() => store.createCommunity(input), c => nav(`/c/${c.slug}`));
    else setErr(r.fields ?? { form: r.error });
  };
  return (
    <div className="narrow col">
      <div className="hero"><span className="line">Communities</span><h1>Find your <Rainbow text="people" />.</h1>
        <p>Your class, your city, a game, a show, a summer. Anyone can start a community and set its rules.</p>
        <div className="row"><button className="btn hot" onClick={() => setOpen(!open)} aria-expanded={open}>Start a community</button></div></div>
      {open && <form className="card form" onSubmit={submit} noValidate>
        <label className="fld"><span>Name</span><input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} maxLength={40} placeholder="Class of 2009, Zurich" aria-invalid={!!err.name} />{err.name && <em>{err.name}</em>}</label>
        <label className="fld"><span>What is it about?</span><textarea rows={2} value={f.description} onChange={e => setF({ ...f, description: e.target.value })} maxLength={200} aria-invalid={!!err.description} />{err.description && <em>{err.description}</em>}</label>
        <label className="fld"><span>Rules, one per line (up to 5)</span><textarea rows={3} value={f.rules} onChange={e => setF({ ...f, rules: e.target.value })} placeholder={'Be kind\nNo full names of people who are not here'} />{err.rules && <em>{err.rules}</em>}</label>
        {err.form && <p className="error">{err.form}</p>}
        <div className="row end"><button type="button" className="btn ghost" onClick={() => setOpen(false)}>Cancel</button><button className="btn hot">Create</button></div>
      </form>}
      <ul className="list">{list.map(c => {
        const members = db.memberships.filter(m => m.communityId === c.id).length;
        const posts = visiblePosts(db).filter(p => p.communityId === c.id).length;
        return (<li key={c.id} className="card comm-row">
          <span className="sq" style={{ color: colorFor(c.slug), borderColor: colorFor(c.slug) }}>{c.name.slice(0, 2).toUpperCase()}</span>
          <div><Link to={`/c/${c.slug}`} className="idea-text">{c.name}</Link><p className="small muted">{c.description}</p>
            <p className="small muted">c/{c.slug} · {members} {members === 1 ? 'member' : 'members'} · {posts} memories {c.example && <ExampleLabel />}</p></div>
        </li>);
      })}</ul>
    </div>
  );
}

export function CommunityPage() {
  const { slug } = useParams();
  const db = useDB(), ui = useUI();
  const c = db.communities.find(x => x.slug === slug);
  const filter = useMemo(() => ({ communityId: c?.id }), [c?.id]);
  if (!c) return <NotFound />;
  const role = memberRole(db, c.id);
  const members = db.memberships.filter(m => m.communityId === c.id).length;
  return (
    <div className="grid">
      <section className="col">
        <div className="hero"><span className="line">c/{c.slug} {c.example && <ExampleLabel />}</span><h1 style={{ color: colorFor(c.slug) }}>{c.name}</h1><p>{c.description}</p>
          <div className="row">
            <button className="btn ghost" aria-pressed={!!role} onClick={() => ui.act(() => store.toggleMembership(c.id), on => ui.toast(on ? `Joined ${c.name}` : `Left ${c.name}`))}>{role ? (role === 'owner' ? 'You started this' : 'Joined') : 'Join'}</button>
            <button className="btn ghost" onClick={() => ui.report('community', c.id)}>Report</button>
          </div></div>
        {role ? <Composer compact defaults={{ communityId: c.id }} key={c.id} /> : <div className="empty">Join to post in this community.</div>}
        <FeedView filter={filter} empty="No memories here yet." endText="You're all caught up in this community." />
      </section>
      <aside className="col side">
        <div className="panel"><h2>Rules</h2>{c.rules.length ? <ol className="rules">{c.rules.map((r, i) => <li key={i}>{r}</li>)}</ol> : <p>No extra rules. The <Link to="/guidelines">community guidelines</Link> always apply.</p>}</div>
        <div className="panel"><h2>About</h2><p>{members} {members === 1 ? 'member' : 'members'} · started {timeAgo(c.createdAt)} by @{userById(db, c.ownerId)?.handle}</p></div>
      </aside>
    </div>
  );
}
